import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye, TrendingUp, Users } from "lucide-react";
import { getCurrentInstagramMe } from "../../../api/AppLinkApi";

interface Props {
  engagement: any;
  platformPostId?: string;
}

export default function InstagramInsightsPanel({
  engagement,
  platformPostId: _platformPostId,
}: Props) {
  const { t } = useTranslation();

  const [igHandle, setIgHandle] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    getCurrentInstagramMe()
      .then((data) => {
        // Expect: username or handle-like field. Fallback to name.
        const handle = data?.username || data?.ig_username || data?.name || "";
        setIgHandle(handle ? (handle.startsWith("@") ? handle : `@${handle}`) : "");
      })
      .catch(() => setIgHandle(""))
      .finally(() => setIsLoading(false));
  }, []);

  // IG engagement payload can differ from Threads. We try common shapes.
  const toNumber = (v: any) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  // Prefer explicit insight fields if backend provides them
  const reach =
    toNumber(engagement?.getIn?.(["metrics", "reach"])) || toNumber(engagement?.reach) || 0;

  const impressions =
    toNumber(engagement?.getIn?.(["metrics", "impressions"])) ||
    toNumber(engagement?.impressions) ||
    0;

  // Fallback engagement: likes + comments if nothing else
  const likes = toNumber(engagement?.getIn?.(["metrics", "like_count"])) || 0;
  const comments = toNumber(engagement?.getIn?.(["metrics", "comments_count"])) || 0;
  const engagementValue =
    toNumber(engagement?.getIn?.(["metrics", "engagement"])) ||
    toNumber(engagement?.engagement) ||
    likes + comments;

  if (isLoading) {
    return (
      <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-3 bg-gray-200 rounded w-full"></div>
            <div className="h-3 bg-gray-200 rounded w-2/3"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-200">
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">
          {t("Post Insights")} {igHandle && `– ${igHandle}`}
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          {t("Insights are fetched live from the Instagram Graph API.")}
        </p>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-700">{t("Reach")}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{reach.toLocaleString()}</div>
          </div>

          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-700">{t("Impressions")}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{impressions.toLocaleString()}</div>
          </div>

          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-gray-700">{t("Engagement")}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {engagementValue.toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
